import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "@core/store/store.ts";
import { fetchStandardDynamicOptions } from "@core/actions/dynamicOptionAction.ts";
import { DynamicOptionResponseDTO } from "@core/modeles/dynamicOption.model.ts";

const STORAGE_KEY = "dynamicOptionsCount";
const STORAGE_VERSION = 5;
export const STANDARD_THRESHOLD = 3;

export interface DynamicOptionsCountState {
  version?: number;
  counts: Record<string, number>;
  originals: Record<string, string>;
  standardByCategory: Record<string, DynamicOptionResponseDTO[]>;
}


export const normalizeLabel = (s: string): string =>
  s.trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

export const makeCountKey = (category: string, label: string): string =>
  `${category}__${normalizeLabel(label)}`;

const loadFromStorage = (): DynamicOptionsCountState => {
  // console.log(`[DynamicOptions] 🔄 loadFromStorage — clé: "${STORAGE_KEY}"`);
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as DynamicOptionsCountState;
      // console.log(
      //   `[DynamicOptions] 📦 localStorage chargé — version: ${parsed.version}, ` +
      //   `counts: ${Object.keys(parsed.counts ?? {}).length} entrée(s), ` +
      //   `originals: ${Object.keys(parsed.originals ?? {}).length} entrée(s), ` +
      //   `standardByCategory: ${Object.keys(parsed.standardByCategory ?? {}).map(
      //     (cat) => `${cat}(${(parsed.standardByCategory ?? {})[cat]?.length ?? 0})`
      //   ).join(", ") || "vide"}`
      // );

      if (!parsed.version || parsed.version < STORAGE_VERSION) {
        let counts: Record<string, number> = { ...(parsed.counts ?? {}) };
        let originals: Record<string, string> = { ...(parsed.originals ?? {}) };
        const CLUSTER_PREFIX = "CLUSTER__";
        const ACCUS_PREFIX   = "ACCUSATION_SUBNATURE__";
        const DYSFUNC_PREFIX = "DYSFUNC_SUBNATURE__";

        const copyEntries = (
          src: Record<string, number>,
          srcOrig: Record<string, string>,
          fromPrefix: string,
          toPrefix: string,
          toCounts: Record<string, number>,
          toOriginals: Record<string, string>
        ) => {
          Object.entries(src).forEach(([key, count]) => {
            if (key.startsWith(fromPrefix)) {
              const suffix = key.slice(fromPrefix.length);
              const newKey = `${toPrefix}${suffix}`;
              toCounts[newKey] = Math.max(toCounts[newKey] ?? 0, count);
              if (srcOrig[key] && !toOriginals[newKey]) {
                toOriginals[newKey] = srcOrig[key];
              }
            }
          });
        };

        const CLAIM_PREFIX = "CLAIM_SUBNATURE__";
        const v = parsed.version ?? 0;

        if (v < 2) {
          copyEntries(parsed.counts ?? {}, parsed.originals ?? {}, CLUSTER_PREFIX, ACCUS_PREFIX, counts, originals);
        }

        if (v === 2) {
          copyEntries(parsed.counts ?? {}, parsed.originals ?? {}, ACCUS_PREFIX, CLUSTER_PREFIX, counts, originals);
        }

        copyEntries(counts, originals, CLUSTER_PREFIX, CLAIM_PREFIX, counts, originals);

        copyEntries(counts, originals, CLUSTER_PREFIX, DYSFUNC_PREFIX, counts, originals);

        const accusSuffixes = new Set(
          Object.keys(counts)
            .filter((k) => k.startsWith(ACCUS_PREFIX))
            .map((k) => k.slice(ACCUS_PREFIX.length))
        );
        [CLUSTER_PREFIX, DYSFUNC_PREFIX, CLAIM_PREFIX].forEach((prefix) => {
          Object.keys(counts).forEach((key) => {
            if (key.startsWith(prefix)) {
              const suffix = key.slice(prefix.length);
              if (accusSuffixes.has(suffix)) {
                delete counts[key];
                delete originals[key];
              }
            }
          });
        });

        const migrated: DynamicOptionsCountState = {
          version: STORAGE_VERSION,
          counts,
          originals,
          standardByCategory: {},
        };
        console.warn(
          `[DynamicOptions] ⚙️ Migration v${parsed.version ?? 0} → v${STORAGE_VERSION} appliquée. ` +
          `counts: ${Object.keys(counts).length} entrée(s)`
        );
        localStorage.setItem(STORAGE_KEY, JSON.stringify(migrated));
        return migrated;
      }

      const result = { ...parsed, standardByCategory: parsed.standardByCategory ?? {} };
      // console.log(
      //   `[DynamicOptions] ✅ État restauré depuis localStorage — ` +
      //   `standardByCategory: ${Object.keys(result.standardByCategory).map(
      //     (cat) => `${cat}(${result.standardByCategory[cat]?.length ?? 0})`
      //   ).join(", ") || "vide"}`
      // );
      return result;
    }
  } catch (err) {
    console.error("[DynamicOptions] ❌ Erreur lecture localStorage:", err);
  }
  console.warn("[DynamicOptions] ⚠️ Aucune donnée en localStorage — état initial vide");
  return { version: STORAGE_VERSION, counts: {}, originals: {}, standardByCategory: {} };
};

const saveToStorage = (state: DynamicOptionsCountState): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...state, version: STORAGE_VERSION }));
    // console.log(
    //   `[DynamicOptions] 💾 saveToStorage — counts: ${Object.keys(state.counts).length}, ` +
    //   `standardByCategory: ${Object.keys(state.standardByCategory).map(
    //     (cat) => `${cat}(${state.standardByCategory[cat]?.length ?? 0})`
    //   ).join(", ") || "vide"}`
    // );
  } catch (err) {
    console.error("[DynamicOptions] ❌ Erreur écriture localStorage:", err);
  }
};


const dynamicOptionsCountSlice = createSlice({
  name: "dynamicOptionsCount",
  initialState: loadFromStorage(),
  reducers: {
    incrementDynamicOption: (
      state,
      action: PayloadAction<{ category: string; label: string }>
    ) => {
      const { category, label } = action.payload;
      const trimmed = label.trim();
      if (!trimmed) return;
      const key = makeCountKey(category, trimmed);
      state.counts[key] = (state.counts[key] ?? 0) + 1;
      if (!state.originals[key]) {
        state.originals[key] = trimmed;
      }
      // console.log(
      //   `[DynamicOptions] ➕ increment — [${category}] "${trimmed}" → count: ${state.counts[key]}` +
      //   (state.counts[key] >= STANDARD_THRESHOLD ? " ✅ STANDARD atteint" : ` (encore ${STANDARD_THRESHOLD - state.counts[key]} soumission(s))`)
      // );
      saveToStorage(state);
    },
    seedCategoryFromSource: (
      state,
      action: PayloadAction<{ sourceCategory: string; targetCategories: string[] }>
    ) => {
      const { sourceCategory, targetCategories } = action.payload;
      const sourcePrefix = `${sourceCategory}__`;
      let changed = false;
      Object.entries(state.counts).forEach(([key, count]) => {
        if (key.startsWith(sourcePrefix)) {
          const suffix = key.slice(sourcePrefix.length);
          targetCategories.forEach((targetCategory) => {
            const targetKey = `${targetCategory}__${suffix}`;
            if ((state.counts[targetKey] ?? 0) < count) {
              state.counts[targetKey] = count;
              if (state.originals[key] && !state.originals[targetKey]) {
                state.originals[targetKey] = state.originals[key];
              }
              changed = true;
            }
          });
        }
      });
      if (changed) {
        // console.log(`[DynamicOptions] 🌱 seedCategoryFromSource — ${sourceCategory} → [${targetCategories.join(", ")}] mis à jour`);
        saveToStorage(state);
      } else {
        // console.log(`[DynamicOptions] 🌱 seedCategoryFromSource — aucun changement pour ${sourceCategory}`);
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchStandardDynamicOptions.fulfilled, (state, action) => {
      const category = action.meta.arg;
      const items = action.payload;
      const standardCount = items.filter((o) => o.standard).length;
      const countQualified = items.filter((o) => !o.standard && o.occurrenceCount >= STANDARD_THRESHOLD).length;
      // console.log(
      //   `[DynamicOptions] 🌐 fetchStandardDynamicOptions fulfilled — catégorie: ${category}, ` +
      //   `${items.length} option(s) reçue(s) dont ${standardCount} standard=true, ${countQualified} avec count>=${STANDARD_THRESHOLD}`
      // );
      const qualifying = items.filter((o) => o.standard || o.occurrenceCount >= STANDARD_THRESHOLD);
      if (qualifying.length > 0) {
        // console.log(
        //   `[DynamicOptions]   └─ labels visibles: ${qualifying.map((o) => `"${o.labelOriginal}"(${o.occurrenceCount}${o.standard ? ",std" : ""})`).join(", ")}`
        // );
      }
      state.standardByCategory[category] = items;
      saveToStorage(state);
    });
  },
});

export const { incrementDynamicOption, seedCategoryFromSource } = dynamicOptionsCountSlice.actions;
export default dynamicOptionsCountSlice.reducer;


export const selectDynamicCount =
  (category: string, label: string) =>
  (state: RootState): number => {
    const key = makeCountKey(category, label);
    return (state.dynamicOptionsCount?.counts ?? {})[key] ?? 0;
  };

export const makeSelectStandardLabels = (category: string) =>
  createSelector(
    (state: RootState) => state.dynamicOptionsCount?.counts ?? {},
    (state: RootState) => state.dynamicOptionsCount?.originals ?? {},
    (state: RootState) => state.dynamicOptionsCount?.standardByCategory ?? {},
    (counts, originals, standardByCategory) => {
      const localLabels = Object.entries(counts)
        .filter(
          ([key, count]) =>
            key.startsWith(`${category}__`) && count >= STANDARD_THRESHOLD
        )
        .map(([key]) => originals[key] ?? key.slice(`${category}__`.length));

      // Labels issus du backend :
      // - soit explicitement marqués standard:true par le backend
      // - soit avec occurrenceCount >= STANDARD_THRESHOLD (même si standard:false,
      //   le backend peut utiliser un critère différent pour setter le flag standard)
      const backendLabels = (standardByCategory[category] ?? [])
        .filter((opt) => opt.standard || opt.occurrenceCount >= STANDARD_THRESHOLD)
        .map((opt) => opt.labelOriginal);

      const seen = new Set(localLabels.map((l) => l.trim().toLowerCase()));
      const merged = [...localLabels];
      for (const label of backendLabels) {
        if (!seen.has(label.trim().toLowerCase())) {
          seen.add(label.trim().toLowerCase());
          merged.push(label);
        }
      }
      // console.log(
      //   `[DynamicOptions] 🔍 makeSelectStandardLabels [${category}] — ` +
      //   `local: [${localLabels.map((l) => `"${l}"`).join(", ") || "—"}], ` +
      //   `backend (std|count>=${STANDARD_THRESHOLD}): [${backendLabels.map((l) => `"${l}"`).join(", ") || "—"}], ` +
      //   `merged: [${merged.map((l) => `"${l}"`).join(", ") || "—"}]`
      // );
      return merged;
    }
  );

export const selectStandardFromBackend = (category: string) =>
  createSelector(
    (state: RootState) => state.dynamicOptionsCount?.standardByCategory ?? {},
    (byCategory) =>
      (byCategory[category] ?? [])
        .filter((opt) => opt.standard || opt.occurrenceCount >= STANDARD_THRESHOLD)
        .map((opt) => opt.labelOriginal)
  );
